import { useState, useEffect, useRef } from "react";
import SaveAndEditChipModal from "./SaveAndEditChipModal.tsx";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { SquarePen, FileMinus, Trash, Upload, ArrowUp, ArrowDown, ArrowUpDown } from "lucide-react";
import { getStockChips, clearStockChipCamara, deleteStockChip, uploadStockChipsCsv } from "../api/chipsEndpoints.api.js";
import { Chip } from "../model/chips";

const StockChipsTable = () => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedChip, setSelectedChip] = useState<Chip | null>(null);
  const [loading, setLoading] = useState(true);
  const [chips, setChips] = useState<Chip[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [uploading, setUploading] = useState(false);
  const [showAll, setShowAll] = useState(false);
  const [sortField, setSortField] = useState<"consumo" | "createdAt" | null>(null);
  const [sortDirection, setSortDirection] = useState<"asc" | "desc">("desc");
  const fileInputRef = useRef<HTMLInputElement>(null);

  const visibleStatuses = ["Activo", "Test", "Desactivado"];

  const handleSort = (field: "consumo" | "createdAt") => {
    if (sortField === field) {
      setSortDirection((prev) => (prev === "asc" ? "desc" : "asc"));
    } else {
      setSortField(field);
      setSortDirection("desc");
    }
  };

  const renderSortIcon = (field: "consumo" | "createdAt") => {
    if (sortField !== field) return <ArrowUpDown className="w-3 h-3 text-gray-300" />;
    return sortDirection === "asc" ? <ArrowUp className="w-3 h-3" /> : <ArrowDown className="w-3 h-3" />;
  };


  const fetchChips = async () => {
    try {
      const data = await getStockChips();
      setChips(data);
    } catch (err) {
      setError("Error al obtener los chips.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchChips();
  }, []);

  const openModal = (chipId?: string) => {
    if (chipId) {
      const found = chips.find(c => c.id === chipId);
      if (found) setSelectedChip(found);
    } else {
      setSelectedChip(null);
    }
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setSelectedChip(null);
    fetchChips();
  };

  const handleClean = async (id: string) => {
    if (window.confirm("¿Estás seguro de que quieres limpiar este chip?")) {
      try {
        setLoading(true);
        await clearStockChipCamara(id);
        toast.success("Chip limpiado correctamente");
        fetchChips();
      } catch (error: any) {
        console.error(error);
        toast.error("Error al limpiar el chip");
        setLoading(false);
      }
    }
  };

  const handleDelete = async (id: string) => {
    if (window.confirm("¿Estás seguro de que quieres eliminar este chip? Esta acción no se puede deshacer.")) {
      try {
        setLoading(true);
        await deleteStockChip(id);
        toast.success("Chip eliminado correctamente");
        fetchChips();
      } catch (error: any) {
        console.error(error);
        toast.error("Error al eliminar el chip");
        setLoading(false);
      }
    }
  };

  const handleUploadCsv = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;

    try {
      setUploading(true);
      const result = await uploadStockChipsCsv(file);
      toast.success(
        `${result.message} — creados: ${result.created}, actualizados: ${result.updated}, retirados: ${result.retired}, saltados: ${result.skipped}`,
        { autoClose: 6000 }
      );
      fetchChips();
    } catch (error: any) {
      console.error(error);
      toast.error("Error al cargar el CSV");
    } finally {
      setUploading(false);
    }
  };

  const statusColors: { [key: string]: string } = {
  'Retirado': 'bg-red-500',
  'Activo': 'bg-green-500',
  'Test': 'bg-blue-500',
  'Desactivado': 'bg-yellow-500'
  };

  const normalizeStatus = (status?: string) => {
    if (!status) return null;
    const match = Object.keys(statusColors).find(
      (key) => key.toLowerCase() === status.toLowerCase()
    );
    return match ?? null;
  };

  const displayedChips = [...chips]
    .filter((chip) => {
      if (showAll) return true;
      const canonicalStatus = normalizeStatus(chip.status);
      return canonicalStatus ? visibleStatuses.includes(canonicalStatus) : false;
    })
    .sort((a, b) => {
      if (!sortField) return 0;
      let aVal: number;
      let bVal: number;
      if (sortField === "consumo") {
        aVal = Number(a.consumo) || 0;
        bVal = Number(b.consumo) || 0;
      } else {
        aVal = a.createdAt ? new Date(a.createdAt).getTime() : 0;
        bVal = b.createdAt ? new Date(b.createdAt).getTime() : 0;
      }
      return sortDirection === "asc" ? aVal - bVal : bVal - aVal;
    });

  return (
    <div className="w-full">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-3xl font-bold">Altas y Edición de Stock de Chips</h1>
        <button
          onClick={() => setShowAll((prev) => !prev)}
          className="bg-gray-700 text-white px-4 py-2 rounded-md shadow-md hover:bg-gray-800 focus:outline-none text-sm"
        >
          {showAll ? "Ver solo Activos, Test y Desactivados" : "Mostrar todos"}
        </button>
      </div>

      <div className="mt-6 flex items-center justify-end gap-3">
        <button
          onClick={() => fileInputRef.current?.click()}
          disabled={uploading}
          className="bg-blue-500 text-white px-6 py-3 rounded-md shadow-md hover:bg-blue-600 focus:outline-none flex items-center gap-2 disabled:bg-blue-300"
          title="El CSV debe ser el export completo: cualquier chip que no aparezca en el archivo queda marcado como retirado"
        >
          <Upload className="w-5 h-5" />
          {uploading ? "Sincronizando..." : "Cargar CSV"}
        </button>
        <input
          ref={fileInputRef}
          type="file"
          accept=".csv"
          onChange={handleUploadCsv}
          className="hidden"
        />

        <button
          onClick={() => openModal()}
          className="bg-green-500 text-white px-6 py-3 rounded-md shadow-md hover:bg-green-600 focus:outline-none flex items-center gap-2"
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 6v6m0 0v6m0-6h6m-6 0H6"/>
          </svg>
          Agregar Nuevo Chip al Stock
        </button>
      </div>

      <p className="mt-2 text-right text-sm text-gray-500">
        ⚠️ Subí siempre el export completo — cualquier chip que falte en el CSV queda marcado como <strong>retirado</strong>.
      </p>

      {loading ? (
        <div className="flex justify-center items-center py-10">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-gray-900"></div>
        </div>
      ) : displayedChips.length === 0 ? (
        <div className="text-center py-10 text-gray-500">
          {chips.length === 0
            ? "No hay chips disponibles en el stock."
            : "No hay chips que coincidan con el filtro actual."}
        </div>
      ) : (
        <div className="overflow-x-auto bg-white shadow-md rounded-lg mt-4">
<table className="min-w-full divide-y divide-gray-200 border-collapse" style={{ borderSpacing: '0 1px' }}>
            <thead className="bg-gray-50 text-sm">
              <tr>
                <th className="px-2 py-2 text-left font-medium text-gray-500 uppercase tracking-wider">Cliente</th>
                <th className="px-2 py-2 text-left font-medium text-gray-500 uppercase tracking-wider">Camara</th>
                <th className="px-2 py-2 text-left font-medium text-gray-500 uppercase tracking-wider">Compañia</th>
                <th className="px-2 py-2 text-left font-medium text-gray-500 uppercase tracking-wider">Nombre Asociado</th>
                <th className="px-2 py-2 text-left font-medium text-gray-500 uppercase tracking-wider">ICCID</th>
                <th className="px-2 py-2 text-left font-medium text-gray-500 uppercase tracking-wider">MSISDN</th>
                <th
                  onClick={() => handleSort("consumo")}
                  className="px-2 py-2 text-left font-medium text-gray-500 uppercase tracking-wider cursor-pointer select-none"
                >
                  <span className="flex items-center gap-1">Consumo {renderSortIcon("consumo")}</span>
                </th>
                <th className="px-2 py-2 text-left font-medium text-gray-500 uppercase tracking-wider">Etiquetas</th>
                <th
                  onClick={() => handleSort("createdAt")}
                  className="px-2 py-2 text-left font-medium text-gray-500 uppercase tracking-wider cursor-pointer select-none"
                >
                  <span className="flex items-center gap-1">Fecha de Alta {renderSortIcon("createdAt")}</span>
                </th>
                <th className="px-2 py-2 text-left font-medium text-gray-500 uppercase tracking-wider">Status</th>
                <th className="px-2 py-2 text-left font-medium text-gray-500 uppercase tracking-wider">Edit</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200 text-sm">
  {displayedChips.map((chip) => (
    <tr key={chip.id}>
      <td className="px-2 py-2 text-gray-700 whitespace-nowrap">{chip.customerId}</td>
      <td className="px-2 py-2 text-gray-700 whitespace-nowrap">{chip.camIdentifier}</td>
      <td className="px-2 py-2 text-gray-700 whitespace-nowrap">{chip.brand}</td>
      <td className="px-2 py-2 text-gray-700 whitespace-nowrap">{chip.associatedName}</td>
      <td className="px-2 py-2 text-gray-700 whitespace-nowrap">{chip.serialNumber}</td>
      <td className="px-2 py-2 text-gray-700 whitespace-nowrap">{chip.phoneNumber}</td>
      <td className="px-2 py-2 text-gray-700 whitespace-nowrap">{chip.consumo}</td>
      <td className="px-2 py-2 text-gray-700 whitespace-nowrap">{chip.etiquetas}</td>
      <td className="px-2 py-2 text-gray-700 whitespace-nowrap">
        {chip.createdAt ? new Date(chip.createdAt).toLocaleDateString("es-AR") : "-"}
      </td>
      <td className="px-2 py-2 text-gray-700 whitespace-nowrap">
  {(() => {
    const canonicalStatus = normalizeStatus(chip.status);
    return (
      <div className="flex items-center gap-2">
        <div
          className={`w-4 h-4 rounded-full flex-shrink-0 ${
            canonicalStatus ? statusColors[canonicalStatus] : "bg-gray-300"
          }`}
        ></div>
        <span>{canonicalStatus ?? chip.status}</span>
      </div>
    );
  })()}
</td>

      <td className="px-2 py-2 whitespace-nowrap">
        <div className="flex items-center gap-3">
          <button
            onClick={() => chip.id && openModal(chip.id)}
            className="p-3.5 lg:p-0 text-gray-600 hover:text-blue-600"
            title="Editar"
          >
            <SquarePen className="w-4 h-4"/>
          </button>
          <button
            onClick={() => chip.id && handleClean(chip.id)}
            className="p-3.5 lg:p-0 text-gray-600 hover:text-orange-600"
            title="Limpiar"
          >
            <FileMinus className="w-4 h-4"/>
          </button>
          <button
            onClick={() => chip.id && handleDelete(chip.id)}
            className="p-3.5 lg:p-0 text-gray-600 hover:text-red-600"
            title="Eliminar"
          >
            <Trash className="w-4 h-4"/>
          </button>
        </div>
      </td>
    </tr>
  ))}
</tbody>
          </table>
        </div>
      )}

      <SaveAndEditChipModal
        isOpen={isModalOpen}
        onClose={closeModal}
        chipData={selectedChip ?? undefined}
      />

      <ToastContainer />
    </div>
  );
};

export default StockChipsTable;
